import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    Host,
    Input,
    OnDestroy,
    OnInit,
    Optional,
} from '@angular/core';
import { AbstractControl, ControlContainer, FormGroupDirective, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';

type ErrorMessageMap = Record<string, string>;

@Directive({
    selector: '[appAutoBindPreviousNgControl]',
    standalone: true,
})
export class AutoBindPreviousNgControlDirective implements AfterViewInit {
    constructor(
        private hostElement: ElementRef<HTMLElement>,
        @Optional() private controlContainer: ControlContainer,
        @Host() private errorComponent: FormControlErrorComponent,
    ) { }

    ngAfterViewInit(): void {
        queueMicrotask(() => {
            const formControlName = this.findPreviousFormControlName();
            if (!formControlName || !this.controlContainer?.control) {
                return;
            }

            const control = this.controlContainer.control.get(formControlName);
            this.errorComponent.bindToControl(control);

            const hostFormElement = this.hostElement.nativeElement.closest('form');
            this.errorComponent.bindToHostFormElement(hostFormElement as HTMLFormElement | null);
        });
    }

    private findPreviousFormControlName(): string | null {
        const previousElement = this.hostElement.nativeElement.previousElementSibling as HTMLElement | null;
        if (!previousElement) {
            return null;
        }

        const directValue = previousElement.getAttribute('formcontrolname')
            ?? previousElement.getAttribute('formControlName')
            ?? previousElement.getAttribute('ng-reflect-form-control-name');
        if (directValue) {
            return directValue;
        }

        const nestedWithControlName = previousElement.querySelector('[formcontrolname], [ng-reflect-form-control-name]') as HTMLElement | null;
        if (!nestedWithControlName) {
            return null;
        }

        return nestedWithControlName.getAttribute('formcontrolname')
            ?? nestedWithControlName.getAttribute('ng-reflect-form-control-name');
    }
}

@Component({
    selector: 'app-form-control-error',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    hostDirectives: [AutoBindPreviousNgControlDirective],
    templateUrl: './form-control-error.component.html',
    styleUrls: ['./form-control-error.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormControlErrorComponent implements OnInit, OnDestroy {
    @Input() controlName?: string;
    @Input() control?: AbstractControl | null;
    @Input() messages?: ErrorMessageMap;

    shouldShowError = false;
    errorMessage = '';

    private controlSubs = new Subscription();
    private lifecycleSubs = new Subscription();
    private resolvedControl: AbstractControl | null = null;
    private hostFormElement: HTMLFormElement | null = null;

    constructor(
        @Optional() private controlContainer: ControlContainer,
        @Optional() private formGroupDirective: FormGroupDirective,
        private cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.bindToControl(this.resolveControl());

        if (this.formGroupDirective) {
            this.lifecycleSubs.add(
                this.formGroupDirective.ngSubmit.subscribe(() => {
                    this.updateErrorState();
                })
            );
        }
    }

    ngOnDestroy(): void {
        this.controlSubs.unsubscribe();
        this.lifecycleSubs.unsubscribe();
    }

    bindToControl(control: AbstractControl | null): void {
        this.controlSubs.unsubscribe();
        this.controlSubs = new Subscription();
        this.resolvedControl = control;

        this.updateErrorState();

        if (!control) {
            return;
        }

        this.controlSubs.add(
            control.statusChanges.subscribe(() => {
                this.updateErrorState();
            })
        );

        this.controlSubs.add(
            control.valueChanges.subscribe(() => {
                this.updateErrorState();
            })
        );
    }

    bindToHostFormElement(formElement: HTMLFormElement | null): void {
        this.hostFormElement = formElement;

        if (!formElement) {
            return;
        }

        this.lifecycleSubs.add(
            new Subscription(() => {
                formElement.removeEventListener('submit', this.handleHostFormSubmit);
            })
        );

        formElement.addEventListener('submit', this.handleHostFormSubmit);
        this.updateErrorState();
    }

    private resolveControl(): AbstractControl | null {
        if (this.control) {
            return this.control;
        }

        if (!this.controlName || !this.controlContainer?.control) {
            return null;
        }

        return this.controlContainer.control.get(this.controlName);
    }

    private updateErrorState(): void {
        const resolvedControl = this.resolvedControl ?? this.resolveControl();
        if (!resolvedControl) {
            this.shouldShowError = false;
            this.errorMessage = '';
            this.cdr.markForCheck();
            return;
        }

        const isFormSubmittedByDirective = this.formGroupDirective?.submitted ?? false;
        const isFormSubmittedByClass = this.hostFormElement?.classList.contains('ng-submitted') ?? false;
        const isFormSubmitted = isFormSubmittedByDirective || isFormSubmittedByClass;
        const hasErrors = resolvedControl.invalid && !!resolvedControl.errors;
        this.shouldShowError = isFormSubmitted && hasErrors;

        if (!this.shouldShowError) {
            this.errorMessage = '';
            this.cdr.markForCheck();
            return;
        }

        this.errorMessage = this.buildMessage(resolvedControl.errors ?? {});
        this.cdr.markForCheck();
    }

    private buildMessage(errors: ValidationErrors): string {
        const firstErrorKey = Object.keys(errors)[0];
        if (!firstErrorKey) {
            return 'Campo inválido.';
        }

        if (this.messages?.[firstErrorKey]) {
            return this.messages[firstErrorKey];
        }

        const defaultMessages: ErrorMessageMap = {
            required: 'Este campo é obrigatório.',
            email: 'Informe um e-mail válido.',
            minlength: `Mínimo de ${errors['minlength']?.requiredLength ?? 0} caracteres.`,
            maxlength: `Máximo de ${errors['maxlength']?.requiredLength ?? 0} caracteres.`,
            min: `O valor mínimo permitido é ${errors['min']?.min}.`,
            max: `O valor máximo permitido é ${errors['max']?.max}.`,
            pattern: 'Formato inválido.',
        };

        return defaultMessages[firstErrorKey] ?? 'Campo inválido.';
    }

    private handleHostFormSubmit = (): void => {
        queueMicrotask(() => {
            this.updateErrorState();
        });
    };
}